import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { API_ACCOUNT_AVATAR } from './EsConstants';
import { serverUrl } from './EsEnv';
import { useEsAccountFace } from './EsAccountFace';
import { useEsStrings } from './EsStrings';

/**
 * The account menu as a circle at the right end of the control row: the avatar, else the Google
 * picture, else the initials, inside a ring of every hue (esSpectrum) that makes it the row's focal point.
 * At a table where the account plays a character the ring is that player's color instead, as wide
 * as a map chip's, so the circle, the Roster swatch, and the chip agree.
 *
 * <p>The popup holds whatever the nav bar passes as children -- its own entries, rendered by
 * the one renderer, so every testid still derives from the entry's text. It is kept MOUNTED
 * and hidden rather than removed, so a presence check on an entry still finds it; `hidden`
 * is display:none, which keeps the closed entries out of the tab order, unlike the palette
 * panel's fade. Dismissed by a click elsewhere, Escape, or a navigation.
 */
export default function EsAccountCircle( { children } ) {
    const t = useEsStrings();
    const face = useEsAccountFace( true );
    const [ open, setOpen ] = useState( false );
    const box = useRef( null );
    const aPath = useLocation().pathname;

    // Choosing an entry navigates, and a navigation closes the popup.
    useEffect( () => {
        setOpen( false );
    }, [ aPath ] );

    useEffect( () => {
        if ( !open ) return undefined;

        const aClick = ( pEvent ) => {
            if ( box.current && !box.current.contains( pEvent.target ) ) setOpen( false );
        };
        const aKey = ( pEvent ) => {
            if ( pEvent.key === 'Escape' ) setOpen( false );
        };

        document.addEventListener( 'mousedown', aClick );
        document.addEventListener( 'keydown', aKey );
        return () => {
            document.removeEventListener( 'mousedown', aClick );
            document.removeEventListener( 'keydown', aKey );
        };
    }, [ open ] );

    return (
        <div ref={ box } className="relative" data-testid="es-nav-account">
            <button
                type="button"
                data-testid="es-nav-account-circle"
                data-tip={ face.displayName || t( 'common-account-circle-tip', 'Account' ) }
                aria-label={ t( 'common-account-circle-menu', 'Account menu' ) }
                aria-expanded={ open }
                onClick={ () => setOpen( ( pWas ) => !pWas ) }
                data-ring={ face.ringColor ?? undefined }
                style={ face.ringColor ? { borderColor: face.ringColor } : undefined }
                className={ ( face.ringColor ? 'border-4' : 'esSpectrum p-[3px]' )
                    + ' esNavDisc ms-1 flex sm:ms-2 cursor-pointer items-center justify-center rounded-full' }>
                { /* The inset disc turns the filled circle into a ring and holds the face on
                     the nav disc ground, so the initials read in light and dark. */ }
                <span className="flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-nav-disc text-nav-glyph hover:bg-nav-disc-hover">
                    { face.avatarRev
                        ? <img src={ serverUrl + API_ACCOUNT_AVATAR + '?v=' + face.avatarRev }
                               alt="" draggable={ false }
                               data-testid="es-nav-account-circle-avatar"
                               className="h-full w-full object-cover" />
                        : face.pictureUrl
                        ? <img src={ face.pictureUrl } alt="" draggable={ false }
                               referrerPolicy="no-referrer"
                               data-testid="es-nav-account-circle-picture"
                               className="h-full w-full object-cover" />
                        : <span data-testid="es-nav-account-circle-initials"
                                className="text-sm font-semibold leading-none">
                            { face.shownInitials }
                        </span> }
                </span>
            </button>

            { /* The entries were built for the bar's row, so the popup stacks them and takes
                 the room the bar's own spacing gave a horizontal list. */ }
            <div
                data-testid="es-nav-account-menu"
                aria-hidden={ !open }
                className={ ( open ? 'block' : 'hidden' )
                    + ' absolute inset-e-0 top-[calc(100%+0.5rem)] z-[99999] w-48 rounded-lg border'
                    + ' border-nav-menu-edge bg-panel py-2 text-sm text-title shadow-lg'
                    + ' [&_.navItem]:mx-4 [&_.navItem]:py-2 lg:[&_.navItem]:mx-4 lg:[&_.navItem]:py-2' }>
                <ul className="block">
                    { children }
                </ul>
            </div>
        </div>
    );
}
